// src/components/AdvancedPWAInstaller.jsx - Enhanced for iOS with better debugging
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Text,
  VStack,
  HStack,
  Icon,
  Flex,
  Circle,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  OrderedList,
  ListItem,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Collapse,
  useDisclosure,
  Image,
  IconButton
} from '@chakra-ui/react';
import {
  FiSmartphone,
  FiMonitor,
  FiX,
  FiCheck,
  FiInfo,
  FiExternalLink,
  FiShare,
  FiPlus,
  FiHome
} from 'react-icons/fi';
import { usePWAInstall } from '../hooks/usePWAInstall';

const AdvancedPWAInstaller = () => {
  const {
    isInstallable,
    isInstalled,
    isInstalling,
    platform,
    installMethod,
    browserSupport,
    canInstall,
    install,
    getInstallInstructions,
    getBrowserSupportInfo
  } = usePWAInstall();

  const [showBanner, setShowBanner] = useState(false);
  const [installSuccess, setInstallSuccess] = useState(false);
  const [userDismissed, setUserDismissed] = useState(false);
  const [showDetailedInstructions, setShowDetailedInstructions] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [bannerAnimation, setBannerAnimation] = useState('hidden');
  const [retryCount, setRetryCount] = useState(0);
  const [debugInfo, setDebugInfo] = useState({});
  
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  // Enhanced onClose to prevent banner from reappearing
  const handleModalClose = useCallback(() => {
    onClose();
    setShowDetailedInstructions(false);
    // Don't show banner again after viewing instructions
    setUserDismissed(true);
    
    // Store dismissal
    const dismissalKey = `pwa-banner-dismissed-${platform}`;
    localStorage.setItem(dismissalKey, JSON.stringify({
      timestamp: Date.now(),
      platform,
      browserSupport,
      retryCount,
      reason: 'viewed_instructions'
    }));
  }, [onClose, platform, browserSupport, retryCount]);
  const bannerRef = useRef(null);

  // Get platform-specific data
  const instructions = getInstallInstructions();
  const supportInfo = getBrowserSupportInfo();
  
  // Enhanced debugging - log everything
  useEffect(() => {
    const debug = {
      timestamp: new Date().toISOString(),
      hookState: {
        isInstallable,
        isInstalled,
        canInstall,
        platform,
        installMethod,
        browserSupport
      },
      supportInfo,
      showBanner,
      userDismissed,
      instructions: instructions?.method,
      bannerConditions: {
        condition1: isInstallable,
        condition2: !userDismissed,
        condition3: !isInstalled,
        condition4: supportInfo.isSupported,
        finalCondition: (isInstallable || (platform === 'ios' && canInstall)) && !userDismissed && !isInstalled
      }
    };
    
    setDebugInfo(debug);
    
    // Special iOS debugging
    if (platform === 'ios') {
    }
  }, [isInstallable, isInstalled, canInstall, platform, installMethod, browserSupport, supportInfo, showBanner, userDismissed, instructions]);

  // Enhanced auto-show banner logic - ONLY FOR MOBILE
  useEffect(() => {
    // Skip desktop completely
    if (platform === 'desktop') {
     
      return;
    }

    // Skip if modal is open (prevents double popup)
    if (isOpen || showDetailedInstructions) {
    
      return;
    }



    // Only show for mobile platforms (iOS and Android)
    const shouldShowBanner = 
      platform !== 'desktop' && (
        ((isInstallable || (platform === 'ios' && canInstall)) && !userDismissed && !isInstalled) ||
        (platform === 'ios' && !window.navigator.standalone && !userDismissed)
      );
    
    if (shouldShowBanner && supportInfo.isSupported) {
     
      
      // Faster appearance for mobile only
      const delays = {
        ios: 500,      
        android: 1000
      };

      const delay = delays[platform] || 1000;
      
      const timer = setTimeout(() => {
       
        setBannerAnimation('entering');
        setTimeout(() => {
          setShowBanner(true);
          setBannerAnimation('visible');
        }, 100);
      }, delay);
      
      return () => clearTimeout(timer);
    }
  }, [isInstallable, userDismissed, isInstalled, platform, supportInfo.isSupported, canInstall, isOpen, showDetailedInstructions]);

  // Force show banner for iOS if conditions are right (debug helper)
  useEffect(() => {
    // Skip if modal is open
    if (isOpen || showDetailedInstructions) {
      return;
    }
    
    if (platform === 'ios' && !window.navigator.standalone && !userDismissed && !showBanner) {
     
      // Force show for iOS in certain conditions
      if (canInstall || isInstallable) {
       
        setTimeout(() => {
          setShowBanner(true);
          setBannerAnimation('visible');
        }, 1000);
      }
    }
  }, [platform, userDismissed, showBanner, canInstall, isInstallable, supportInfo, isOpen, showDetailedInstructions]);

  // Handle installation with enhanced error recovery
  const handleInstall = useCallback(async () => {
    
    setCurrentStep(0);
    
    if (platform === 'ios' || installMethod === 'manual') {
     
      
      // Kapamak için banner'ı gizle
      setShowBanner(false);
      setBannerAnimation('leaving');
      
      setShowDetailedInstructions(true);
      
      
      onOpen();
      return;
    }

    try {
      const result = await install();
     
      
      if (result === true) {
        setInstallSuccess(true);
        setBannerAnimation('success');
        setShowBanner(false);
        localStorage.removeItem('pwa-banner-dismissed');
        
        setTimeout(() => {
          setInstallSuccess(false);
        }, 3000);
        
        toast({
          title: 'Installed! 🎉',
          description: 'Wells is now on your home screen',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right'
        });
      } else if (result === 'manual') {
        // Kapamak için banner'ı gizle
        setShowBanner(false);
        setBannerAnimation('leaving');
        
        setShowDetailedInstructions(true);
        onOpen();
      } else {
       
        
        if (retryCount < 2) {
          setRetryCount(prev => prev + 1);
          toast({
            title: 'Try Again?',
            description: 'Installation not completed',
            status: 'warning',
            duration: 4000,
            isClosable: true,
            position: 'top-right'
          });
        }
      }
    } catch (error) {
     
      
      toast({
        title: 'Installation Error',
        description: 'See manual instructions instead',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right'
      });
      
      setShowDetailedInstructions(true);
      onOpen();
    }
  }, [platform, installMethod, install, retryCount, onOpen, toast]);

  // Enhanced banner dismiss with platform-specific tracking
  const handleDismiss = useCallback(() => {
    
    setBannerAnimation('leaving');
    
    setTimeout(() => {
      setShowBanner(false);
      setUserDismissed(true);
    }, 300);
    
    const dismissalKey = `pwa-banner-dismissed-${platform}`;
    localStorage.setItem(dismissalKey, JSON.stringify({
      timestamp: Date.now(),
      platform,
      browserSupport,
      retryCount
    }));

    if (window.gtag) {
      window.gtag('event', 'pwa_banner_dismissed', {
        platform,
        browser_support: browserSupport,
        retry_count: retryCount
      });
    }
  }, [platform, browserSupport, retryCount]);

  // Check previous dismissals with platform awareness
  useEffect(() => {
    const dismissalKey = `pwa-banner-dismissed-${platform}`;
    const dismissed = localStorage.getItem(dismissalKey);
    
    if (dismissed) {
      try {
        const { timestamp, retryCount: previousRetries } = JSON.parse(dismissed);
        const dayInMs = 24 * 60 * 60 * 1000;
        
        if (Date.now() - timestamp < dayInMs) {
         
          setUserDismissed(true);
          setRetryCount(previousRetries || 0);
        } else {
        
          localStorage.removeItem(dismissalKey);
        }
      } catch (error) {
        localStorage.removeItem(dismissalKey);
      }
    }
  }, [platform]);

  // Get enhanced platform icons and styling
  const getPlatformConfig = useCallback(() => {
    switch (platform) {
      case 'ios':
        return {
          icon: FiSmartphone,
          color: 'blue.400',
          bgGradient: 'linear(135deg, blue.400, blue.600)',
          name: 'iOS',
          emoji: '📱'
        };
      case 'android':
        return {
          icon: FiSmartphone,
          color: 'green.400',
          bgGradient: 'linear(135deg, green.400, green.600)',
          name: 'Android',
          emoji: '🤖'
        };
      default:
        return {
          icon: FiMonitor,
          color: 'purple.400',
          bgGradient: 'linear(135deg, purple.400, purple.600)',
          name: 'Desktop',
          emoji: '💻'
        };
    }
  }, [platform]);


  // Enhanced render condition - MOBILE ONLY
  const shouldRender = () => {
    // Never show on desktop
    if (platform === 'desktop') {
      return false;
    }
    
    // Always show for iOS if not installed and not dismissed
    if (platform === 'ios' && !window.navigator.standalone && !userDismissed) {
      return true;
    }
    
    // Standard conditions for mobile platforms
    return canInstall && !isInstalled && supportInfo.isSupported;
  };



  if (!shouldRender()) {

    return null;
  }



  return (
    <>
      {/* Debug info in development */}
      {process.env.NODE_ENV === 'development' && platform !== 'desktop' && (
        <Box
          position="fixed"
          top="10px"
          right="10px"
          bg="blackAlpha.800"
          color="white"
          p={2}
          borderRadius="md"
          fontSize="xs"
          maxW="300px"
          zIndex={10001}
        >
          <Text fontWeight="bold">PWA Debug:</Text>
          <Text>Platform: {platform}</Text>
          <Text>Can Install: {canInstall ? 'Yes' : 'No'}</Text>
          <Text>Show Banner: {showBanner ? 'Yes' : 'No'}</Text>
          <Text>Is Installed: {isInstalled ? 'Yes' : 'No'}</Text>
          <Text>Support: {supportInfo.level}</Text>
        </Box>
      )}

      {/* Minimal Mobile Banner - Bottom Strip */}
      <Collapse 
        in={showBanner} 
        animateOpacity 
        transition={{ enter: { duration: 0.3 }, exit: { duration: 0.2 } }}
      >
        <Box
          ref={bannerRef}
          position="fixed"
          bottom="0"
          left="0"
          right="0"
          bg="rgba(15, 27, 14, 0.95)"
          backdropFilter="blur(12px)"
          borderTopWidth="1px"
          borderColor="rgba(78, 224, 53, 0.2)"
          py={{ base: 2, md: 3 }}
          px={{ base: 3, md: 4 }}
          boxShadow="0 -2px 10px rgba(0, 0, 0, 0.3)"
          zIndex={9999}
          className={`pwa-installer-banner animation-${bannerAnimation}`}
          role="dialog"
          aria-labelledby="pwa-banner-title"
        >
          <Flex align="center" justify="space-between" maxW="100%" mx="auto">
            {/* Left side - Logo and Text */}
            <HStack spacing={{ base: 2, md: 3 }} flex="1" minW={0}>
              {/* Small Logo */}
              <Box
                w={{ base: "32px", md: "40px" }}
                h={{ base: "32px", md: "40px" }}
                bg="linear-gradient(135deg, #22c55e, #15803d)"
                borderRadius="md"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
              >
                <Image 
                  src="/assets/light_logo.png" 
                  alt="Wells" 
                  w={{ base: "20px", md: "24px" }}
                  h={{ base: "20px", md: "24px" }}
                />
              </Box>
              
              {/* Minimal Text */}
              <Text 
                id="pwa-banner-title"
                fontWeight="semibold" 
                color="white" 
                fontSize={{ base: "sm", md: "md" }}
                noOfLines={1}
              >
                Install Wells App
              </Text>
            </HStack>
            
            {/* Right side - Actions */}
            <HStack spacing={{ base: 1, md: 2 }}>
              {/* Install Button - Very Compact */}
              <Button
                size="xs"
                colorScheme="green"
                onClick={handleInstall}
                isLoading={isInstalling}
                borderRadius="md"
                bg="green.600"
                _hover={{
                  bg: "green.700"
                }}
                px={{ base: 3, md: 4 }}
                h={{ base: "28px", md: "32px" }}
                fontSize={{ base: "xs", md: "sm" }}
                fontWeight="500"
                minW="fit-content"
              >
                {platform === 'ios' ? 'Guide' : 'Install'}
              </Button>
              
              {/* Close Button - Very Small */}
              <IconButton
                size="xs"
                variant="ghost"
                onClick={handleDismiss}
                color="gray.400"
                _hover={{ color: "white", bg: "whiteAlpha.100" }}
                icon={<Icon as={FiX} boxSize={{ base: 3, md: 4 }} />}
                aria-label="Close"
                borderRadius="md"
                minW={{ base: "28px", md: "32px" }}
                h={{ base: "28px", md: "32px" }}
              />
            </HStack>
          </Flex>
        </Box>
      </Collapse>

      {/* Enhanced Instructions Modal */}
      <Modal 
        isOpen={isOpen} 
        onClose={handleModalClose} 
        size="lg" 
        isCentered
        closeOnOverlayClick={true}
      >
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <ModalContent
          bg="rgba(15, 27, 14, 0.98)"
          borderWidth="1px"
          borderColor="rgba(78, 224, 53, 0.4)"
          borderRadius="2xl"
          mx={4}
          maxH="85vh"
          overflowY="auto"
        >
          <ModalHeader
            color="white"
            fontSize="xl"
            fontWeight="bold"
            borderBottomWidth="1px"
            borderBottomColor="rgba(78, 224, 53, 0.2)"
            pb={4}
          >
            <HStack spacing={3}>
              <Text fontSize="2xl">📱</Text>
              <VStack align="start" spacing={0}>
                <Text>Install Wells</Text>
                <Text fontSize="sm" fontWeight="normal" color="gray.400">
                  Add to your home screen
                </Text>
              </VStack>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="gray.400" onClick={handleModalClose} />
          
          <ModalBody py={6}>
            <VStack spacing={6} align="stretch">
              {/* Platform-specific alerts */}
              {platform === 'ios' && (
                <Alert
                  status="info"
                  bg="rgba(59, 130, 246, 0.1)"
                  borderColor="rgba(59, 130, 246, 0.3)"
                  borderWidth="1px"
                  borderRadius="xl"
                >
                  <AlertIcon color="blue.400" />
                  <Box>
                    <AlertTitle color="blue.300" fontSize="sm">
                      {instructions.method === 'ios-other' ? 'Safari Required' : 'Safari Installation'}
                    </AlertTitle>
                    <AlertDescription color="gray.300" fontSize="sm">
                      {instructions.requirements}
                    </AlertDescription>
                  </Box>
                </Alert>
              )}

              {/* Benefits Section */}
              
              {/* Step by step instructions */}
              <Box>
                <Text color="white" fontSize="md" fontWeight="semibold" mb={4}>
                  Installation Steps:
                </Text>
                <OrderedList spacing={4} color="gray.300">
                  {instructions.steps.map((step, index) => (
                    <ListItem 
                      key={index} 
                      fontSize="sm"
                      p={3}
                      bg={currentStep === index ? "rgba(34, 197, 94, 0.1)" : "transparent"}
                      borderRadius="lg"
                      borderWidth="1px"
                      borderColor={currentStep === index ? "rgba(34, 197, 94, 0.3)" : "transparent"}
                      transition="all 0.2s"
                    >
                      <HStack spacing={3}>
                        {instructions.icons && (
                          <Text fontSize="lg">{instructions.icons[index]}</Text>
                        )}
                        <Text flex="1">{step}</Text>
                      </HStack>
                    </ListItem>
                  ))}
                </OrderedList>
              </Box>

              {/* Visual guide for iOS */}
              {platform === 'ios' && (
                <Box
                  p={4}
                  bg="rgba(0, 0, 0, 0.3)"
                  borderRadius="xl"
                  textAlign="center"
                >
                  <Text color="white" fontSize="sm" fontWeight="medium" mb={3}>
                    Visual Guide:
                  </Text>
                  <HStack justify="center" spacing={4} mb={3}>
                    <VStack spacing={1}>
                      <Circle size="40px" bg="blue.500" color="white">
                        <Icon as={FiShare} boxSize={5} />
                      </Circle>
                      <Text fontSize="xs" color="gray.400">Share</Text>
                    </VStack>
                    <Icon as={FiExternalLink} color="gray.400" transform="rotate(90deg)" />
                    <VStack spacing={1}>
                      <Circle size="40px" bg="green.500" color="white">
                        <Icon as={FiPlus} boxSize={5} />
                      </Circle>
                      <Text fontSize="xs" color="gray.400">Add</Text>
                    </VStack>
                    <Icon as={FiExternalLink} color="gray.400" transform="rotate(90deg)" />
                    <VStack spacing={1}>
                      <Circle size="40px" bg="purple.500" color="white">
                        <Icon as={FiHome} boxSize={5} />
                      </Circle>
                      <Text fontSize="xs" color="gray.400">Home</Text>
                    </VStack>
                  </HStack>
                  <Text fontSize="xs" color="gray.500">
                    Tap Share → Add to Home Screen → Add
                  </Text>
                </Box>
              )}

              {/* Help section */}
              <Box
                p={4}
                bg="rgba(78, 224, 53, 0.05)"
                borderWidth="1px"
                borderColor="rgba(78, 224, 53, 0.2)"
                borderRadius="xl"
              >
                <HStack spacing={2} mb={2}>
                  <Icon as={FiInfo} color="green.400" />
                  <Text color="green.300" fontSize="sm" fontWeight="medium">
                    Need Help?
                  </Text>
                </HStack>
                <Text fontSize="xs" color="gray.400" lineHeight="1.5">
                  If you're having trouble, try refreshing the page or check that you're using a supported browser.
                  The app will work normally in your browser even without installation.
                </Text>
              </Box>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Enhanced Success Animation */}
      {installSuccess && (
        <Box
          position="fixed"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          bg="rgba(15, 27, 14, 0.97)"
          backdropFilter="blur(24px)"
          borderWidth="1px"
          borderColor="rgba(34, 197, 94, 0.4)"
          borderRadius="2xl"
          p={8}
          textAlign="center"
          zIndex={10000}
          className="success-animation"
        >
          <VStack spacing={4}>
            <Box position="relative">
              <Circle size="100px" bg="green.500" color="white">
                <Icon as={FiCheck} boxSize={12} />
              </Circle>
              <Box
                position="absolute"
                inset={0}
                borderRadius="full"
                border="4px solid"
                borderColor="green.400"
                className="success-ring-animation"
              />
            </Box>
            <VStack spacing={2}>
              <Text color="white" fontSize="xl" fontWeight="bold">
                Installed! 🎉
              </Text>
              <Text color="gray.300" fontSize="sm" textAlign="center">
                Wells is now on your home screen
              </Text>
            </VStack>
          </VStack>
        </Box>
      )}

      {/* Enhanced CSS Animations - Same as before */}
      <style jsx global>{`
        .animation-entering {
          animation: slideUpBounce 0.4s ease-out;
        }

        .animation-visible {
          animation: none;
        }

        .animation-leaving {
          animation: slideDown 0.3s ease-in;
        }

        .animation-success {
          animation: successScale 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        @keyframes slideUpBounce {
          0% {
            opacity: 0;
            transform: translateY(100%);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideDown {
          0% {
            opacity: 1;
            transform: translateY(0);
          }
          100% {
            opacity: 0;
            transform: translateY(100%);
          }
        }

        @keyframes successScale {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
          100% {
            transform: scale(1);
          }
        }

        .shine-animation {
          animation: none;
        }

        @media (min-width: 768px) {
          .shine-animation {
            animation: shine 4s infinite;
          }
        }

        @keyframes shine {
          0% {
            transform: translateX(-100%) translateY(-100%) rotate(45deg);
          }
          100% {
            transform: translateX(100%) translateY(100%) rotate(45deg);
          }
        }

        .success-animation {
          animation: successPulse 2s ease-out;
        }

        .success-ring-animation {
          animation: ringPulse 2s ease-out;
        }

        @keyframes successPulse {
          0% {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.5);
          }
          50% {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1.1);
          }
          100% {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }

        @keyframes ringPulse {
          0% {
            opacity: 0;
            transform: scale(0.8);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.2);
          }
          100% {
            opacity: 0;
            transform: scale(1.5);
          }
        }

        .pwa-installer-banner {
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          transition: all 0.3s ease;
        }

        @media (max-width: 768px) {
          .pwa-installer-banner {
            border-radius: 0 !important;
            bottom: 0 !important;
            left: 0 !important;
            right: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding-bottom: calc(8px + env(safe-area-inset-bottom)) !important;
          }
        }

        @supports (-webkit-touch-callout: none) {
          .pwa-installer-banner {
            padding-bottom: calc(12px + env(safe-area-inset-bottom));
          }
        }

        @media screen and (max-width: 768px) and (orientation: portrait) {
          .pwa-installer-banner {
            bottom: 0 !important;
            left: 0 !important;
            right: 0 !important;
            margin: 0 !important;
            border-radius: 0 !important;
            border-top-left-radius: 16px !important;
            border-top-right-radius: 16px !important;
          }
        }

        @media (max-width: 768px) {
          .pwa-installer-banner {
            max-width: 100% !important;
            margin: 0 !important;
            border-radius: 0 !important;
            border-top-left-radius: 16px !important;
            border-top-right-radius: 16px !important;
            bottom: 0 !important;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .pwa-installer-banner,
          .pwa-installer-banner *,
          .shine-animation,
          .success-animation,
          .success-ring-animation,
          [class*="animation"] {
            animation: none !important;
            transition: none !important;
          }
        }

        @media (prefers-contrast: high) {
          .pwa-installer-banner {
            background: #000000 !important;
            border: 2px solid #ffffff !important;
          }
        }

        .pwa-installer-banner button:focus-visible,
        .pwa-installer-banner [role="button"]:focus-visible {
          outline: 2px solid #22c55e !important;
          outline-offset: 2px !important;
          box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.3) !important;
        }

        @media (prefers-color-scheme: dark) {
          .pwa-installer-banner {
            background: rgba(0, 0, 0, 0.97);
            border-color: rgba(78, 224, 53, 0.5);
          }
        }

        @media print {
          .pwa-installer-banner,
          .success-animation {
            display: none !important;
          }
        }

        .pwa-installer-banner {
          will-change: transform, opacity;
          contain: layout style paint;
        }

        .pwa-installer-banner button[disabled] {
          pointer-events: none;
          opacity: 0.6;
        }
      `}</style>
    </>
  );
};

export default AdvancedPWAInstaller;